import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, AlertCircle, Trash2, Ban, Filter, Bell, Globe, Send, Search, Zap } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/seo";
import AdminTabs from "@/components/AdminTabs";

const KIND_FILTERS = ["all", "ban", "rate_limit", "login_fail", "unban"];

export default function AdminSecurity() {
  const { user } = useAuth();
  const [bans, setBans] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [live, setLive] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState("");
  const [kind, setKind] = useState("all");

  // manual ban form
  const [manualIp, setManualIp] = useState("");
  const [manualReason, setManualReason] = useState("");
  const [manualSeconds, setManualSeconds] = useState(0);

  // AbuseIPDB lookup
  const [lookupIp, setLookupIp] = useState("");
  const [lookupResult, setLookupResult] = useState(null);

  useSEO({ title: "Admin · Security", noindex: true });

  const reload = async (k = kind) => {
    setError("");
    try {
      const [a, b, c, d, e] = await Promise.all([
        api.get("/admin/security/bans?limit=200"),
        api.get(`/admin/security/events?limit=200${k !== "all" ? `&kind=${k}` : ""}`),
        api.get("/admin/security/stats"),
        api.get("/admin/security/integrations"),
        api.get("/admin/security/live"),
      ]);
      setBans(a.data?.items || []);
      setEvents(b.data?.items || []);
      setStats(c.data || null);
      setIntegrations(d.data || null);
      setLive(e.data || null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => {
    reload(kind);
    // Auto-refresh "live" indicators every 15s for ops-room feel
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/security/live");
        setLive(data || null);
      } catch { /* ignore */ }
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [kind]);

  const unban = async (ip) => {
    if (!window.confirm(`Unban ${ip}?`)) return;
    setBusy(ip);
    try { await api.post("/admin/security/unban", { ip, reason: "admin review" }); await reload(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy("");
  };

  const ban = async (e) => {
    e.preventDefault();
    if (!manualIp.trim() || !manualReason.trim()) return;
    setBusy("ban");
    try {
      await api.post("/admin/security/ban", { ip: manualIp.trim(), reason: manualReason.trim(), ban_seconds: Number(manualSeconds) || 0 });
      setManualIp(""); setManualReason("");
      await reload();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy("");
  };

  const sendTest = async () => {
    setBusy("test"); setInfo(""); setError("");
    try {
      const { data } = await api.post("/admin/security/test-webhook");
      setInfo(data.sent ? "✅ Test alert delivered to your webhook." : "⚠️ Webhook accepted the request but didn't confirm delivery.");
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy("");
  };

  const lookup = async () => {
    if (!lookupIp.trim()) return;
    setBusy("lookup"); setLookupResult(null); setError("");
    try {
      const { data } = await api.post("/admin/security/abuseipdb-check", { ip: lookupIp.trim() });
      setLookupResult(data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy("");
  };

  if (user && user.role !== "admin") {
    return <div className="min-h-screen dotted-bg flex items-center justify-center px-5"><div className="brutal-card bg-white p-6 max-w-md w-full"><h1 className="display text-2xl font-extrabold mb-2">Admins only</h1><Link to="/profiles" className="pill-btn primary">Back</Link></div></div>;
  }

  return (
    <div className="min-h-screen dotted-bg" data-testid="admin-security-page">
      <header className="px-4 sm:px-6 py-3 border-b-[3px] border-[color:var(--ink)] bg-[color:var(--cream)] flex items-center gap-3">
        <Link to="/profiles" className="pill-btn"><ArrowLeft size={16} /> Back</Link>
        <div className="display text-lg sm:text-xl font-extrabold flex items-center gap-2">
          <ShieldAlert size={20} /> Admin · security
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <AdminTabs current="security" />

        {live?.under_attack && (
          <div className="brutal-card p-4 mb-5 flex items-center gap-3 animate-pulse" style={{ background: "var(--coral)", color: "#fff" }} data-testid="under-attack-banner">
            <Zap size={28} />
            <div className="flex-1">
              <div className="display text-xl font-extrabold leading-tight">⚠️ UNDER ATTACK</div>
              <div className="text-sm opacity-90">{live.bans_60s} bans in last 60s · {live.rate_limits_60s} rate-limits · {live.bans_5m} in last 5 min. Defenses are holding.</div>
            </div>
            {live.top_attackers_1h?.length > 0 && (
              <div className="text-xs font-mono text-right">
                Top attackers (1h):<br />
                {live.top_attackers_1h.slice(0, 3).map((a) => (
                  <div key={a.ip}>{a.ip} ({a.bans})</div>
                ))}
              </div>
            )}
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="security-stats">
            <Stat label="Active bans"      value={stats.active_bans} highlight={stats.active_bans > 0} />
            <Stat label="Permanent bans"   value={stats.permanent_bans} />
            <Stat label="Bans today"       value={stats.bans_today} />
            <Stat label="Events today"     value={stats.events_today} />
          </div>
        )}

        {error && <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3 mb-4 flex items-start gap-2" data-testid="security-error"><AlertCircle size={16} className="mt-0.5" /> {error}</div>}
        {info && <div className="brutal-card bg-[#E5F4DD] text-[color:var(--ink)] p-3 mb-4" data-testid="security-info">{info}</div>}

        {/* Integration status */}
        {integrations && (
          <div className="brutal-card bg-white p-4 mb-6 grid sm:grid-cols-2 gap-4" data-testid="integrations-panel">
            <div>
              <div className="display text-xs uppercase tracking-[0.25em] text-[color:var(--ink-soft)] font-bold mb-1 flex items-center gap-2"><Bell size={14} /> Webhook alerts</div>
              {integrations.webhook_configured ? (
                <>
                  <div className="display font-extrabold text-lg">{integrations.webhook_kind?.toUpperCase() || "Custom"} <span className="text-[color:var(--coral)]">●</span> live</div>
                  <button type="button" onClick={sendTest} disabled={busy === "test"} className="pill-btn mt-2 text-xs" data-testid="webhook-test"><Send size={12} /> Send test alert</button>
                </>
              ) : (
                <div className="text-sm text-[color:var(--ink-soft)]">Not configured. Set <code className="font-mono text-xs bg-[color:var(--cream-deep)] px-1 rounded">SECURITY_WEBHOOK_URL</code> in backend env (Slack or Discord) to get pinged on every attack.</div>
              )}
            </div>
            <div>
              <div className="display text-xs uppercase tracking-[0.25em] text-[color:var(--ink-soft)] font-bold mb-1 flex items-center gap-2"><Globe size={14} /> AbuseIPDB</div>
              {integrations.abuseipdb_configured ? (
                <>
                  <div className="display font-extrabold text-lg"><span className="text-[color:var(--coral)]">●</span> active</div>
                  <div className="text-xs text-[color:var(--ink-soft)]">Auto-bans IPs scoring ≥75. Auto-reports confirmed attackers.</div>
                </>
              ) : (
                <div className="text-sm text-[color:var(--ink-soft)]">Not configured. Set <code className="font-mono text-xs bg-[color:var(--cream-deep)] px-1 rounded">ABUSEIPDB_API_KEY</code> for IP-reputation auto-ban + reporting (free tier: 1k req/day).</div>
              )}
            </div>
          </div>
        )}

        <h2 className="display text-2xl font-extrabold mb-3">Active bans</h2>
        {bans.length === 0 ? (
          <div className="brutal-card bg-white p-5 text-[color:var(--ink-soft)] mb-8" data-testid="bans-empty">No active bans. The internet is calm. 🌤️</div>
        ) : (
          <div className="brutal-card bg-white p-0 overflow-x-auto mb-8">
            <table className="w-full text-sm" data-testid="bans-table">
              <thead className="bg-[color:var(--paper-muted)] text-left">
                <tr>
                  <th className="p-3 display font-bold">IP</th>
                  <th className="p-3 display font-bold">Reason</th>
                  <th className="p-3 display font-bold">Banned at</th>
                  <th className="p-3 display font-bold">Expires</th>
                  <th className="p-3 display font-bold">Path</th>
                  <th className="p-3 display font-bold"></th>
                </tr>
              </thead>
              <tbody>
                {bans.map((b, i) => (
                  <tr key={b.ip} className={i % 2 === 0 ? "bg-white" : "bg-[color:var(--cream)]"} data-testid={`ban-row-${b.ip}`}>
                    <td className="p-2 font-mono text-xs">{b.ip}</td>
                    <td className="p-2">{b.reason}</td>
                    <td className="p-2 text-xs text-[color:var(--ink-soft)] whitespace-nowrap">{new Date(b.banned_at).toLocaleString()}</td>
                    <td className="p-2 text-xs">{b.ban_seconds === 0 ? <span className="font-bold text-[color:var(--coral-deep)]">PERMANENT</span> : (b.expires_at ? new Date(b.expires_at).toLocaleString() : "—")}</td>
                    <td className="p-2 text-xs font-mono text-[color:var(--ink-soft)]">{(b.trigger_path || "—").slice(0, 40)}</td>
                    <td className="p-2">
                      <button type="button" disabled={busy === b.ip} onClick={() => unban(b.ip)} className="pill-btn text-xs" data-testid={`unban-${b.ip}`}>
                        <Trash2 size={12} /> Unban
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h2 className="display text-2xl font-extrabold mb-3 flex items-center gap-2">Recent events <span className="text-sm font-normal text-[color:var(--ink-soft)]">(last 200)</span></h2>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Filter size={16} className="text-[color:var(--ink-soft)]" />
          {KIND_FILTERS.map((k) => (
            <button key={k} type="button" onClick={() => setKind(k)} className={`pill-btn text-xs ${kind === k ? "gold" : ""}`} data-testid={`event-filter-${k}`}>{k}</button>
          ))}
        </div>
        {events.length === 0 ? (
          <div className="brutal-card bg-white p-5 text-[color:var(--ink-soft)] mb-8" data-testid="events-empty">No events match this filter.</div>
        ) : (
          <div className="brutal-card bg-white p-0 overflow-x-auto mb-8">
            <table className="w-full text-sm" data-testid="events-table">
              <thead className="bg-[color:var(--paper-muted)] text-left">
                <tr>
                  <th className="p-3 display font-bold">When</th>
                  <th className="p-3 display font-bold">Kind</th>
                  <th className="p-3 display font-bold">IP</th>
                  <th className="p-3 display font-bold">Reason</th>
                  <th className="p-3 display font-bold">Method/Path</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={`${e.ts}-${e.ip}-${i}`} className={i % 2 === 0 ? "bg-white" : "bg-[color:var(--cream)]"} data-testid={`event-row-${i}`}>
                    <td className="p-2 text-[color:var(--ink-soft)] font-mono text-xs whitespace-nowrap">{new Date(e.ts).toLocaleString()}</td>
                    <td className="p-2 font-bold text-xs">{e.kind}</td>
                    <td className="p-2 font-mono text-xs">{e.ip}</td>
                    <td className="p-2 text-xs">{e.reason}</td>
                    <td className="p-2 font-mono text-[10px] text-[color:var(--ink-soft)]">{e.method} {(e.path || "").slice(0, 50)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h2 className="display text-2xl font-extrabold mb-3 flex items-center gap-2"><Ban size={20} /> Manual ban</h2>
        <form onSubmit={ban} className="brutal-card bg-white p-5 grid sm:grid-cols-[1fr_2fr_1fr_auto] gap-3 items-end mb-8" data-testid="manual-ban-form">
          <label className="block">
            <span className="display font-extrabold text-xs uppercase tracking-[0.2em] mb-1 block">IP</span>
            <input type="text" required value={manualIp} onChange={(e) => setManualIp(e.target.value)} className="brutal-input" placeholder="203.0.113.42" data-testid="manual-ip" />
          </label>
          <label className="block">
            <span className="display font-extrabold text-xs uppercase tracking-[0.2em] mb-1 block">Reason</span>
            <input type="text" required value={manualReason} onChange={(e) => setManualReason(e.target.value)} className="brutal-input" placeholder="why this IP is being banned" data-testid="manual-reason" />
          </label>
          <label className="block">
            <span className="display font-extrabold text-xs uppercase tracking-[0.2em] mb-1 block">Duration (sec, 0 = ∞)</span>
            <input type="number" min="0" value={manualSeconds} onChange={(e) => setManualSeconds(e.target.value)} className="brutal-input" data-testid="manual-seconds" />
          </label>
          <button type="submit" disabled={busy === "ban" || !manualIp.trim() || !manualReason.trim()} className="pill-btn danger" data-testid="manual-ban-submit">
            <Ban size={14} /> Ban
          </button>
        </form>

        <h2 className="display text-2xl font-extrabold mb-3 flex items-center gap-2"><Search size={20} /> Look up an IP on AbuseIPDB</h2>
        <div className="brutal-card bg-white p-5 mb-8" data-testid="abuseipdb-lookup">
          <div className="flex flex-wrap gap-2 items-end mb-3">
            <label className="block flex-1 min-w-[200px]">
              <span className="display font-extrabold text-xs uppercase tracking-[0.2em] mb-1 block">IP</span>
              <input type="text" value={lookupIp} onChange={(e) => setLookupIp(e.target.value)} className="brutal-input" placeholder="8.8.8.8" data-testid="lookup-ip" />
            </label>
            <button type="button" onClick={lookup} disabled={busy === "lookup" || !lookupIp.trim() || !integrations?.abuseipdb_configured} className="pill-btn primary" data-testid="lookup-go">
              <Search size={14} /> {integrations?.abuseipdb_configured ? "Look up" : "Needs key"}
            </button>
          </div>
          {lookupResult && (
            <div className="grid sm:grid-cols-3 gap-3 text-sm" data-testid="lookup-result">
              <div className={`brutal-card p-3 ${lookupResult.abuse_confidence_score >= 75 ? "bg-[color:var(--coral)] text-white" : (lookupResult.abuse_confidence_score >= 25 ? "bg-[color:var(--gold)]" : "bg-white")}`}>
                <div className="text-[10px] uppercase font-bold opacity-90">Confidence</div>
                <div className="display text-3xl font-extrabold">{lookupResult.abuse_confidence_score}/100</div>
              </div>
              <div className="brutal-card bg-white p-3">
                <div className="text-[10px] uppercase font-bold text-[color:var(--ink-soft)]">Country / ISP</div>
                <div className="font-bold">{lookupResult.country_code || "—"}</div>
                <div className="text-xs text-[color:var(--ink-soft)] truncate">{lookupResult.isp || "—"}</div>
              </div>
              <div className="brutal-card bg-white p-3">
                <div className="text-[10px] uppercase font-bold text-[color:var(--ink-soft)]">Reports</div>
                <div className="display font-extrabold text-2xl">{lookupResult.total_reports}</div>
                {lookupResult.is_tor && <div className="text-xs text-[color:var(--coral-deep)] font-bold">⚠ Tor exit node</div>}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, highlight = false }) {
  // Inline styles bypass any Tailwind JIT class-detection issues with arbitrary
  // CSS-var values + dynamic class concatenation.
  const bg = highlight ? "var(--coral)" : "#ffffff";
  const labelColor = highlight ? "rgba(255,255,255,0.9)" : "var(--ink-soft)";
  const valueColor = highlight ? "#ffffff" : "var(--ink)";
  return (
    <div className="brutal-card p-3" style={{ background: bg }}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: labelColor }}>{label}</div>
      <div className="display text-3xl font-extrabold leading-none mt-1" style={{ color: valueColor }}>{value ?? "—"}</div>
    </div>
  );
}
